import type { IntegrationWithChannelDataResponse } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import type { BlockContext, BlockRule, BlockedResult } from "../types";

type EmailChannel = "gmail" | "outlook";
type EmailIntegration = Extract<
  IntegrationWithChannelDataResponse,
  { integrationType: EmailChannel }
>;

const EMAIL_SETTINGS_HREF = "/settings/channels/supportEmail";
const MANAGE_INTEGRATIONS_LABEL = "Manage integrations";

function isEmailChannel(channel: string): channel is EmailChannel {
  return channel === "gmail" || channel === "outlook";
}

function findThreadEmailIntegration(
  ctx: BlockContext,
): EmailIntegration | undefined {
  if (!ctx.thread || !ctx.integrations) return undefined;
  const threadIntegrationId = ctx.thread.integrationId;
  return ctx.integrations.find(
    (i): i is EmailIntegration =>
      (i.integrationType === "gmail" || i.integrationType === "outlook") &&
      i.id === threadIntegrationId,
  );
}

/**
 * Block sending when the gmail/outlook integration this thread was received on
 * is no longer connected to the merchant (deleted or never existed for the
 * logged-in team). Mirrors v1's "Email integration not found" case.
 */
export const emailIntegrationMissingRule: BlockRule = (
  ctx: BlockContext,
): BlockedResult | null => {
  if (!isEmailChannel(ctx.channel)) return null;
  // Without data, don't block optimistically — the editor stays usable until
  // we know for sure. The send-path already rejects orphaned integrations.
  if (ctx.integrations === undefined || ctx.thread === undefined) return null;

  const integration = findThreadEmailIntegration(ctx);
  if (integration) return null;

  return {
    reason: "email-integration-missing",
    message:
      "Email integration not found. The email account for this conversation is no longer connected.",
    action: { label: MANAGE_INTEGRATIONS_LABEL, href: EMAIL_SETTINGS_HREF },
  };
};

/**
 * Block sending when the email provider has revoked Redo's permissions.
 * Mirrors v1's "permissionsRevoked" check in `useEmailIntegrationStatus`.
 */
export const emailIntegrationRevokedRule: BlockRule = (
  ctx: BlockContext,
): BlockedResult | null => {
  if (!isEmailChannel(ctx.channel)) return null;
  if (ctx.integrations === undefined || ctx.thread === undefined) return null;

  const integration = findThreadEmailIntegration(ctx);
  if (!integration || !integration.permissionsRevoked) return null;

  const provider =
    integration.integrationType === "gmail" ? "Gmail" : "Outlook";
  return {
    reason: "email-integration-permissions-revoked",
    message: `${provider} integration has lost permissions. Please reconnect your ${provider} account in email settings.`,
    action: { label: MANAGE_INTEGRATIONS_LABEL, href: EMAIL_SETTINGS_HREF },
  };
};
